import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getMyOrders } from '../utils/api';
import LoginSignupDialog from '../components/LoginSignupDialog';


const toDateWithTimeStr = (d) => {
  if (!d) return '—';
  const x = typeof d === 'string' ? new Date(d) : d;
  if (!x.toLocaleString) return '—';
  return x.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const AccountPage = () => {
  const { user, loading: authLoading, updateProfile, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [dob, setDob] = useState('');
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
      setAddress(user.address || '');
      if (user.dob) {
        const raw = user.dob;
        let ymd = '';
        if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) ymd = raw.slice(0, 10);
        else {
          const x = new Date(raw);
          if (!isNaN(x.getTime())) ymd = x.toISOString().slice(0, 10);
        }
        setDob(ymd);
      } else {
        setDob('');
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setOrders([]);
      setOrdersLoading(false);
      return;
    }
    setOrdersLoading(true);
    getMyOrders()
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : data?.orders ?? data?.data ?? [];
        setOrders(Array.isArray(list) ? list : []);
      })
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess(false);
    setProfileSaving(true);
    try {
      await updateProfile({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: address.trim(),
        dob: dob.trim() || null,
      });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ pt: 10, pb: 8, display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
        <CircularProgress sx={{ color: 'var(--color-accent)' }} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ pt: 10, pb: 8, px: 3, maxWidth: 500, mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontFamily: "'Cormorant Garamond', Georgia, serif", mb: 2 }}>
          Account
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Log in to view your profile and bookings.
        </Typography>
        <Button variant="contained" onClick={() => setLoginOpen(true)} sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-charcoal)' } }}>
          Login
        </Button>
        <LoginSignupDialog open={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={() => setLoginOpen(false)} />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 10, pb: 8, px: 3, minHeight: '100vh', bgcolor: 'var(--color-cream)' }}>
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography variant="h4" sx={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, color: 'var(--color-charcoal)' }}>
            Account
          </Typography>
          <Button
            variant="outlined"
            onClick={() => logout()}
            sx={{
              borderColor: 'var(--color-sand)',
              color: 'var(--color-charcoal)',
              '&:hover': { borderColor: 'var(--color-charcoal)', color: 'var(--color-charcoal)' },
            }}
          >
            Logout
          </Button>
        </Box>

        <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid var(--color-sand)', borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Profile</Typography>
          {profileError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError('')}>{profileError}</Alert>}
          {profileSuccess && <Alert severity="success" sx={{ mb: 2 }}>Profile updated.</Alert>}
          <Box component="form" onSubmit={handleSaveProfile} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
            <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
            <TextField label="Address / City" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
            <TextField
              label="Date of birth"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
            <Button type="submit" variant="contained" disabled={profileSaving} sx={{ alignSelf: 'flex-start', bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-charcoal)' } }}>
              {profileSaving ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </Box>
        </Paper>

        <Typography sx={{ fontWeight: 600, mb: 2 }}>My bookings</Typography>
        {ordersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: 'var(--color-accent)' }} />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary">No bookings yet. <Link to="/book">Book a room</Link>.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {orders.map((order) => {
              const roomName = order.roomName || order.roomId?.name || '—';
              const isConfirmed = order.status === 'confirmed';
              return (
                <Paper key={order._id} elevation={0} sx={{ p: 2, border: '1px solid var(--color-sand)', borderRadius: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{roomName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check-in: {toDateWithTimeStr(order.checkinDate)} · Check-out: {toDateWithTimeStr(order.checkoutDate)}
                      {order.orderId && ` · ${order.orderId}`}
                    </Typography>
                    <Chip label={isConfirmed ? 'Confirmed' : 'Unconfirmed'} size="small" color={isConfirmed ? 'success' : 'default'} sx={{ mt: 0.5 }} />
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default AccountPage;
